import apiClient from '@src/api/clients';

const BASE_URL = 'client-inbox/email-messages/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    ),
  );

export const listEmailMessages = (params = {}) =>
  apiClient.request({ url: BASE_URL, method: 'GET', payload: cleanParams(params) });

export const retrieveEmailMessage = (id, params = {}) =>
  apiClient.request({ url: `${BASE_URL}${id}/`, method: 'GET', payload: cleanParams(params) });

export const updateEmailMessage = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}${id}/`, method: 'PATCH', payload });

export const listEmailSenders = () =>
  apiClient.request({ url: `${BASE_URL}senders/`, method: 'GET' });

export const triggerIngest = () =>
  apiClient.request({ url: `${BASE_URL}ingest/`, method: 'POST', payload: {} });

export const replyToEmailMessage = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}${id}/reply/`, method: 'POST', payload });

export const createBudgetConfirmations = (payload) =>
  apiClient.request({ url: `${BASE_URL}budget-confirmations/`, method: 'POST', payload });

export const inspectEmailThreadContext = (id, params = {}) =>
  apiClient.request({
    url: `${BASE_URL}${id}/inspect-context/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const regenerateEmailThreadDraft = (id, payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}${id}/regenerate-draft/`,
    method: 'POST',
    payload,
  });

export const polishDraft = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/polish-draft/`,
    method: 'POST',
    payload,
  });

export const quickCapture = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/quick-capture/`,
    method: 'POST',
    payload,
  });

export const scheduleReply = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/schedule-reply/`,
    method: 'POST',
    payload,
  });

export const getScheduledReplies = (id) =>
  apiClient.request({
    url: `${BASE_URL}${id}/scheduled-replies/`,
    method: 'GET',
  });

export const cancelScheduledReply = (id, scheduledReplyId) =>
  apiClient.request({
    url: `${BASE_URL}${id}/scheduled-replies/`,
    method: 'DELETE',
    payload: { scheduled_reply_id: scheduledReplyId },
  });

export const getAiAccuracyTrends = (params = {}) =>
  apiClient.request({
    url: `${BASE_URL}ai-accuracy-trends/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const getDashboardSummary = (params = {}) =>
  apiClient.request({ url: `${BASE_URL}dashboard-summary/`, method: 'GET', payload: cleanParams(params) });

export const updateConversationStatus = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}${id}/update-status/`, method: 'POST', payload });

export const markThreadRead = (id) =>
  apiClient.request({ url: `${BASE_URL}${id}/mark-read/`, method: 'POST', payload: {} });

export const toggleReplyExample = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/toggle-reply-example/`,
    method: 'POST',
    payload,
  });

export const claimThread = (id) =>
  apiClient.request({ url: `${BASE_URL}${id}/claim/`, method: 'POST', payload: {} });

// Emails the AI decided need no reply — they never became tasks.
export const listNoReplyThreads = (params = {}) =>
  apiClient.request({
    url: `${BASE_URL}no-reply/`,
    method: 'GET',
    payload: cleanParams(params),
  });

// Overrule "no reply needed": drafts a reply and creates the task.
export const markThreadNeedsReply = (id) =>
  apiClient.request({ url: `${BASE_URL}${id}/needs-reply/`, method: 'POST', payload: {} });

export const listStaffActions = (id) =>
  apiClient.request({ url: `${BASE_URL}${id}/staff-actions/`, method: 'GET' });

export const runStaffAction = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/run-action/`,
    method: 'POST',
    payload,
  });

export const reassignThreadToClient = (id, payload) =>
  apiClient.request({
    url: `${BASE_URL}${id}/reassign-to-client/`,
    method: 'POST',
    payload,
  });

export const forwardThread = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}${id}/forward/`, method: 'POST', payload });

export const listThreadNotes = (id) =>
  apiClient.request({ url: `${BASE_URL}${id}/notes/`, method: 'GET' });

export const createThreadNote = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}${id}/notes/`, method: 'POST', payload });

export const bulkAction = (payload) =>
  apiClient.request({ url: `${BASE_URL}bulk-actions/`, method: 'POST', payload });

export const composeEmail = (payload) =>
  apiClient.request({ url: `${BASE_URL}compose/`, method: 'POST', payload });

const PROPOSALS_URL = 'client-inbox/knowledge-proposals/';

export const listKnowledgeProposals = (params = {}) =>
  apiClient.request({ url: PROPOSALS_URL, method: 'GET', payload: cleanParams(params) });

export const approveProposal = (id) =>
  apiClient.request({ url: `${PROPOSALS_URL}${id}/approve/`, method: 'POST', payload: {} });

export const dismissProposal = (id) =>
  apiClient.request({ url: `${PROPOSALS_URL}${id}/dismiss/`, method: 'POST', payload: {} });

const CHAT_URL = 'client-inbox/conversation-turns/';
const SESSIONS_URL = 'client-inbox/chat-sessions/';

export const sendChatMessage = (payload) =>
  apiClient.request({ url: `${CHAT_URL}chat/`, method: 'POST', payload });

/**
 * Stream a chat reply via SSE.
 *
 * Returns a Promise that resolves when the stream actually ends (either
 * normally or due to error). Calls `onEvent(event)` for every parsed SSE
 * frame: {type: "session"|"tool_start"|"text_delta"|"done"|"error", ...}.
 *
 * The caller can call `.cancel()` on the returned promise (attached as a
 * property) to abort the in-flight request.
 *
 * Uses native fetch + ReadableStream because the axios wrapper buffers
 * the full response (not suitable for streaming). Auth comes from the
 * same localStorage access_token the rest of the API client uses.
 */
export const streamChatMessage = (payload, onEvent) => {
  const controller = new AbortController();

  const promise = (async () => {
    // Lazy import — only this code path needs the raw axios instance to
    // pull baseURL from. The axios wrapper is unsuitable for SSE because
    // it buffers responses.
    const { defaultAxiosClient, getViewAsClientId } = await import('@src/api/clients/default');
    const baseUrl = defaultAxiosClient.defaults.baseURL;
    // Raw fetch bypasses the axios wrapper that attaches the staff preview
    // header, so mid screen-share the chat used to answer as staff with the
    // staff tools. Carrying the header makes the server refuse the write
    // (409) like every other call while a preview is on.
    const viewAsClientId = getViewAsClientId();
    const auth = (() => {
      try {
        return JSON.parse(localStorage.getItem('auth') || '{}');
      } catch (_e) {
        return {};
      }
    })();

    let response;
    try {
      response = await fetch(`${baseUrl}/${CHAT_URL}chat-stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth.access_token ? { Authorization: `Bearer ${auth.access_token}` } : {}),
          ...(viewAsClientId ? { 'X-View-As-Client': viewAsClientId } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') {
        onEvent({ type: 'error', message: err?.message || 'Network error' });
      }
      return;
    }

    if (!response.ok || !response.body) {
      let detail = `HTTP ${response.status}`;
      let reason;
      let retryAfterSeconds;
      try {
        const data = await response.json();
        if (data?.detail) detail = data.detail;
        if (data?.reason) reason = data.reason;
        if (data?.retry_after_seconds != null) retryAfterSeconds = data.retry_after_seconds;
      } catch (_e) { /* swallow */ }
      onEvent({
        type: 'error',
        message: detail,
        status: response.status,
        reason,
        retry_after_seconds: retryAfterSeconds,
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (err) {
        if (err?.name !== 'AbortError') {
          onEvent({ type: 'error', message: err?.message || 'Stream read error' });
        }
        break;
      }
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });

      // SSE frames are separated by a blank line. Each frame has one or
      // more `data: <json>` lines.
      let frameEnd;
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);
        const dataLines = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart());
        if (!dataLines.length) continue;
        const raw = dataLines.join('\n');
        try {
          const parsed = JSON.parse(raw);
          onEvent(parsed);
        } catch (_e) {
          // Ignore malformed frames — the server should never emit them
          // but we don't want one bad frame to break the stream.
        }
      }
    }
  })();

  // Attach a cancel method to the returned promise so callers can abort.
  promise.cancel = () => controller.abort();
  return promise;
};

export const getChatHistory = (params = {}) =>
  apiClient.request({ url: `${CHAT_URL}history/`, method: 'GET', payload: cleanParams(params) });

export const listChatSessions = (params = {}) =>
  apiClient.request({ url: SESSIONS_URL, method: 'GET', payload: cleanParams(params) });

export const createChatSession = (payload) =>
  apiClient.request({ url: SESSIONS_URL, method: 'POST', payload });

export const closeChatSession = (id) =>
  apiClient.request({ url: `${SESSIONS_URL}${id}/close/`, method: 'POST', payload: {} });

export const fetchAgentUsageStats = (params = {}) =>
  apiClient.request({
    url: `${SESSIONS_URL}usage-stats/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const fetchAgentUsageSessions = (params = {}) =>
  apiClient.request({
    url: `${SESSIONS_URL}usage-sessions/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const fetchAgentUsageByTopic = (params = {}) =>
  apiClient.request({
    url: `${SESSIONS_URL}usage-by-topic/`,
    method: 'GET',
    payload: cleanParams(params),
  });

const ANOMALIES_URL = 'client-inbox/chat-anomaly-findings/';

export const listChatAnomalyFindings = (params = {}) =>
  apiClient.request({
    url: ANOMALIES_URL,
    method: 'GET',
    payload: cleanParams(params),
  });

export const updateChatAnomalyFinding = (id, payload) =>
  apiClient.request({ url: `${ANOMALIES_URL}${id}/`, method: 'PATCH', payload });

export default {
  listEmailMessages,
  retrieveEmailMessage,
  updateEmailMessage,
  listEmailSenders,
  triggerIngest,
  replyToEmailMessage,
  createBudgetConfirmations,
  inspectEmailThreadContext,
  regenerateEmailThreadDraft,
  polishDraft,
  quickCapture,
  scheduleReply,
  getScheduledReplies,
  cancelScheduledReply,
  getAiAccuracyTrends,
  getDashboardSummary,
  updateConversationStatus,
  markThreadRead,
  toggleReplyExample,
  claimThread,
  listNoReplyThreads,
  markThreadNeedsReply,
  listStaffActions,
  runStaffAction,
  reassignThreadToClient,
  forwardThread,
  listThreadNotes,
  createThreadNote,
  bulkAction,
  composeEmail,
  listKnowledgeProposals,
  approveProposal,
  dismissProposal,
  sendChatMessage,
  streamChatMessage,
  getChatHistory,
  listChatSessions,
  createChatSession,
  closeChatSession,
  fetchAgentUsageStats,
  fetchAgentUsageSessions,
  fetchAgentUsageByTopic,
  listChatAnomalyFindings,
  updateChatAnomalyFinding,
};
